import CurrencyContext from "./CurrencyContext";
import { useEffect, useReducer, useContext,useRef } from "react";
import ErrorMessageContext from './ErrorMessageContext';
import { getIpInformation } from "../utils/ipInformation";

// Constants
const DEFAULT_CURRENCY = "USD";
const DEFAULT_LANGUAGE = "en";
const LANGUAGES = ['fr', 'en'];
const CURRENCY_SYMBOL_JSON = "api/world_currency_symbols.json";

// Helper functions
const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    currency: urlParams.get('currency')?.toUpperCase() || null,
    language: urlParams.get('language')?.toLowerCase() || DEFAULT_LANGUAGE,
  };
};

const urlParams = getUrlParams();

const initialCurrencyState = {
  isLoaded: true,
  error: null,
  loadedCurrencies: {
    'USD': { Symbol: '$', Locale: 'en-US' },
  },
  conversionRates: {
    'USD': 1,
  },
  languageParentWindow: urlParams.language,
  selectedCurrency: urlParams.currency || 'USD',
  currentConversionRate: 1,
  locale: 'en-US',  
  isInitialized: false
};

// Reducer
const currencyReducer = (state, action) => {
  switch (action.type) {
    case 'CHANGE_CURRENCY':
      return {
        ...state,
        selectedCurrency: action.currency,
        currentConversionRate: state.conversionRates[action.currency],
        locale: state.loadedCurrencies && 
                action.currency && 
                state.loadedCurrencies[action.currency] ? 
                  state.loadedCurrencies[action.currency].Locale || 'en-US' : 'en-US'
      };
    case 'CHANGE_LANGUAGE':
      return {
        ...state,
        languageParentWindow: action.language
      };
    case 'INIT':
      return {
        ...state,
        isLoaded: true,
        isInitialized: true,
        loadedCurrencies: action.loadedCurrencies,
        conversionRates: action.conversionRates,
        selectedCurrency: action.selectedCurrency,
        currentConversionRate: action.currentConversionRate,
        languageParentWindow: action.languageParentWindow,
        locale: action.loadedCurrencies && 
                action.selectedCurrency && 
                action.loadedCurrencies[action.selectedCurrency] ? 
                action.loadedCurrencies[action.selectedCurrency].Locale || 'en-US' : 'en-US'
      };
    case 'ERROR':
      return { ...state, error: action.error, isLoaded: true };
    default:
      return state;
  }
};

const CurrencyProvider = ({ children }) => {
  const errorMessageCtx = useContext(ErrorMessageContext);
  const [currencyState, dispatchCurrencyAction] = useReducer(currencyReducer, initialCurrencyState);
  const hasFetchedRef = useRef(false);

  const changeCurrencyHandler = (currency) => {
    dispatchCurrencyAction({ type: "CHANGE_CURRENCY", currency });
  };

  const changeLanguageHandler = (language) => {
    dispatchCurrencyAction({ type: "CHANGE_LANGUAGE", language });
  };

  const currencyContext = {
    ...currencyState,
    changeCurrency: changeCurrencyHandler,
    changeLanguage: changeLanguageHandler,
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
  
    const fetchData = async () => {
      try {
        const fetchOptions = {
          headers: { 'Accept': 'application/json' },
          redirect: 'follow',
        };
  
        let detectedCurrency = null;
  
        const geoData = await getIpInformation();
  
        if (geoData?.country_code) {
          const fallbackMap = {
            'CA': 'CAD',
            'US': 'USD',
            'GB': 'GBP',
            'AU': 'AUD',
            'EU': 'EUR',
            'FR': 'EUR',
            'DE': 'EUR',
            'JP': 'JPY'
          };
          detectedCurrency = fallbackMap[geoData.country_code] || DEFAULT_CURRENCY;
        }
  
        const [exchangeRateResponse, currencySymbolsResponse] = await Promise.all([
          fetch(process.env.REACT_APP_EXCHANGE_RATE, fetchOptions),
          fetch(CURRENCY_SYMBOL_JSON, fetchOptions)
        ]);
  
        const [exchangeRateData, currencySymbolsData] = await Promise.all([
          exchangeRateResponse.json(),
          currencySymbolsResponse.json()
        ]);
  
        const loadedCurrencies = Object.fromEntries(
          Object.entries(currencySymbolsData).map(([key, value]) => [
            value.Code, { Symbol: value.Symbol, Locale: value.Locale }
          ])
        );
  
        const conversionRates = exchangeRateData.conversion_rates;
  
        let currency = urlParams.currency || detectedCurrency || DEFAULT_CURRENCY;
  
        if (!conversionRates[currency] || !loadedCurrencies[currency]) {
          currency = DEFAULT_CURRENCY;
        }
  
        let language = currencyState.languageParentWindow;
        if (!LANGUAGES.includes(language)) {
          errorMessageCtx.addErrorMessage(`"${language}" non reconnu pour data-language, remplacé par "${DEFAULT_LANGUAGE}"`);
          language = DEFAULT_LANGUAGE;
        }
  
        dispatchCurrencyAction({
          type: "INIT",
          loadedCurrencies,
          conversionRates,
          selectedCurrency: currency,
          currentConversionRate: conversionRates[currency],
          languageParentWindow: language
        });
      } catch (error) {
        dispatchCurrencyAction({ type: "ERROR", error });
      }
    };
  
    fetchData();
  }, [currencyState.languageParentWindow, errorMessageCtx]);
  

  return (
    <CurrencyContext.Provider value={currencyContext}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyProvider;